import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'

export interface IAuthService {
  getUser(): Promise<User | null>
  getSignedIn(): Promise<boolean>
  signIn(): Promise<void>
  signOut(): Promise<void>
  getLogoutURI(): Promise<string>
  getIdToken(): Promise<string | null>
  signinRedirectCallback(): Promise<User>
  activateAccount(): Promise<void>
  changePassword(): Promise<void>
  resetMFA(): Promise<void>
}

export default class AuthService implements IAuthService {
  private readonly userManager: UserManager
  private readonly policyLogin: string
  private readonly policyActivateAccount: string
  private readonly policyChangePassword: string
  private readonly policyResetMFA: string

  constructor() {
    this.policyLogin = import.meta.env.VITE_SSO_POLICY_LOGIN
    this.policyActivateAccount = import.meta.env.VITE_SSO_POLICY_ACTIVATE_ACCOUNT
    this.policyChangePassword = import.meta.env.VITE_SSO_POLICY_CHANGE_PASSWORD
    this.policyResetMFA = import.meta.env.VITE_SSO_POLICY_EDIT_QR

    const settings = {
      response_mode: 'query' as const,
      authority: import.meta.env.VITE_SSO_AUTHORITY,
      client_id: import.meta.env.VITE_SSO_CLIENT_ID,
      redirect_uri: `${globalThis.origin}/callback`,
      automaticSilentRenew: true,
      response_type: 'code',
      scope: `openid ${import.meta.env.VITE_SSO_CLIENT_ID}`,
      post_logout_redirect_uri: `${globalThis.origin}`,
      filterProtocolClaims: true,
      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: globalThis.localStorage }),
    }

    this.userManager = new UserManager(settings)
  }

  async getUser(): Promise<User | null> {
    try {
      const user = await this.userManager.getUser()
      if (user?.expired) {
        return this.userManager.signinSilent().catch((error) => {
          console.error('Silent renew failed:', error)
          this.signIn()
          return null
        })
      }
      return user
    } catch (error) {
      console.error('getUser error:', error)
      return null
    }
  }

  async getSignedIn(): Promise<boolean> {
    try {
      const user = await this.getUser()
      if (user == null) {
        await this.signIn()
        return false
      }
      return true
    } catch {
      return false
    }
  }

  async signIn(): Promise<void> {
    try {
      await this.userManager.signinRedirect()
    } catch (error) {
      console.error('signIn error:', error)
    }
  }

  async signOut(): Promise<void> {
    try {
      await this.userManager.signoutRedirect()
    } catch (error) {
      console.error('signOut error:', error)
    }
  }

  async getLogoutURI(): Promise<string> {
    try {
      const metadata = await this.userManager.metadataService.getMetadata()
      return metadata.end_session_endpoint ?? '/'
    } catch (error) {
      console.error('getLogoutURI error:', error)
      return '/'
    }
  }

  async getIdToken(): Promise<string | null> {
    try {
      const user = await this.getUser()
      return user?.id_token ?? null
    } catch (error) {
      console.error('getIdToken error:', error)
      return null
    }
  }

  async signinRedirectCallback(): Promise<User> {
    return this.userManager.signinRedirectCallback()
  }

  async activateAccount(): Promise<void> {
    await this.b2CAction(this.policyActivateAccount)
  }

  async changePassword(): Promise<void> {
    await this.b2CAction(this.policyChangePassword)
  }

  async resetMFA(): Promise<void> {
    await this.b2CAction(this.policyResetMFA)
  }

  private async b2CAction(policy: string): Promise<void> {
    try {
      const metadata = await this.userManager.metadataService.getMetadata()
      metadata.authorization_endpoint = metadata.authorization_endpoint?.replaceAll(
        this.policyLogin,
        policy,
      )
      await this.signIn()
    } catch (error) {
      console.error('b2CAction error:', error)
    }
  }
}
